"use client";
import React from "react";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import { ArrowRight, Bookmark, CalendarCheck, Check, Clock, Globe, MapPin, Navigation, Phone, Search, Share2, Star } from "lucide-react";
import MarketingNavBar from "@/components/MarketingNavBar";

const bg = "#111827";                       // gray-900
const bgCard = "#1f2937";                   // gray-800
const bgCardAlt = "#374151";                // gray-700
const border = "#374151";                   // gray-700
const borderSubtle = "rgba(75,85,99,0.4)"; // gray-600/40 — matches the calendar grid
const textPrimary = "#ffffff";
const textSecondary = "#d1d5db";            // gray-300
const textMuted = "#9ca3af";                // gray-400
const textDim = "#6b7280";                  // gray-500
const green = "#34D399";                    // emerald-400
const blue = "#60A5FA";                     // blue-400

const BookingWidget: React.FC = () => {
  const t = useTranslations("Landing");
  const days = t.raw("Widgets.Booking.Days") as string[];
  const times = ["17:00", "18:00", "19:00", "20:00"];
  const booked = new Set(["0-1", "1-3", "3-0", "2-4", "0-3"]);
  const selected = "2-2";

  return (
    <div style={{ background: bgCard, border: `1px solid ${border}`, borderRadius: "1rem", padding: "1.5rem", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.6)" }}>
      <div style={{ borderBottom: `1px solid ${borderSubtle}`, paddingBottom: "1rem", marginBottom: "1rem" }}>
        <p style={{ color: textMuted, fontSize: "0.75rem", marginBottom: "0.35rem" }}>{t("Widgets.Booking.VenueName")} · {t("Widgets.Booking.Court")}</p>
        <div className="flex items-center justify-between">
          <p style={{ color: textPrimary, fontWeight: 600, fontSize: "0.9rem" }}>{t("Widgets.Booking.Details")}</p>
          <span style={{ background: "rgba(62,207,148,0.12)", color: green, fontSize: "0.68rem", padding: "0.2rem 0.6rem", borderRadius: "9999px", fontWeight: 600, letterSpacing: "0.04em" }}>{t("Widgets.Booking.Live")}</span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "3rem repeat(5, 1fr)", gap: "0.35rem", marginBottom: "0.5rem" }}>
        <div />
        {days.map(d => (
          <div key={d} style={{ color: textDim, fontSize: "0.68rem", fontWeight: 500, textAlign: "center" }}>{d}</div>
        ))}
      </div>

      {times.map((time, ti) => (
        <div key={time} style={{ display: "grid", gridTemplateColumns: "3rem repeat(5, 1fr)", gap: "0.35rem", marginBottom: "0.35rem" }}>
          <div style={{ color: textDim, fontSize: "0.68rem", display: "flex", alignItems: "center" }}>{time}</div>
          {[0, 1, 2, 3, 4].map(ci => {
            const key = `${ti}-${ci}`;
            const isSelected = key === selected;
            const isBusy = booked.has(key);
            return (
              <div key={ci} style={{
                height: "2rem",
                borderRadius: "0.375rem",
                background: isSelected ? green : isBusy ? borderSubtle : "rgba(62,207,148,0.06)",
                border: isSelected ? "none" : `1px solid ${borderSubtle}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.65rem",
                color: isSelected ? "#0e1014" : textDim,
                fontWeight: isSelected ? 700 : 400,
                cursor: isSelected ? "pointer" : "default",
              }}>
                {isSelected ? "✓" : ""}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const SLOT_H = 38;

const ReservationWidget: React.FC = () => {
  const t = useTranslations("Landing");
  // Named by sport, not "Court 1/2/3": the calendar is the multi-sport pitch,
  // and a numbered list reads like the venue only does one thing.
  const courts = t.raw("Widgets.Reservations.Courts") as string[];
  const timeSlots = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00"];

  const reservations = [
    { court: 0, start: 0, end: 2, name: "Ion M." },
    { court: 1, start: 1, end: 3, name: "Ana P." },
    { court: 2, start: 0, end: 1, name: "Club FC" },
    { court: 0, start: 3, end: 5, name: "Maria C." },
    { court: 2, start: 2, end: 4, name: "Radu S." },
    { court: 1, start: 4, end: 6, name: "Bogdan T." },
  ];

  const totalH = timeSlots.length * SLOT_H;

  return (
    <div style={{ background: bgCard, border: `1px solid ${border}`, borderRadius: "1rem", overflow: "hidden", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.6)" }}>
      {/* Header */}
      <div style={{ borderBottom: `1px solid ${borderSubtle}`, padding: "0.7rem 1rem", display: "flex", alignItems: "center", justifyContent: "space-between", background: bgCardAlt }}>
        <div className="flex items-center gap-2">
          <span style={{ color: textSecondary, fontSize: "0.78rem", fontWeight: 600 }}>{t("Widgets.Reservations.VenueName")}</span>
          <span style={{ color: textDim, fontSize: "0.75rem" }}>·</span>
          <span style={{ color: green, fontSize: "0.75rem", fontWeight: 600 }}>{t("Widgets.Reservations.Today")}</span>
        </div>
        <div className="flex items-center gap-1">
          {["‹", "›"].map(ch => (
            <span key={ch} style={{ color: textMuted, background: border, borderRadius: "0.25rem", padding: "0.1rem 0.45rem", fontSize: "0.8rem", cursor: "pointer" }}>{ch}</span>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div style={{ display: "flex" }}>
        {/* Time column */}
        <div style={{ width: "3.4rem", flexShrink: 0, borderRight: `1px solid ${borderSubtle}` }}>
          <div style={{ height: "2rem", borderBottom: `1px solid ${borderSubtle}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: textDim, fontSize: "0.62rem" }}>{t("Widgets.Reservations.TimeColumn")}</span>
          </div>
          <div style={{ position: "relative", height: `${totalH}px` }}>
            {timeSlots.map((slot, i) => (
              <div key={slot} style={{ position: "absolute", top: `${i * SLOT_H}px`, width: "100%", borderBottom: `1px solid ${borderSubtle}`, height: `${SLOT_H}px`, display: "flex", alignItems: "flex-start", justifyContent: "flex-end", paddingRight: "0.4rem", paddingTop: "0.2rem" }}>
                <span style={{ color: textDim, fontSize: "0.62rem" }}>{slot}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Court columns */}
        {courts.map((court, ci) => (
          <div key={court} style={{ flex: 1, borderRight: ci < courts.length - 1 ? `1px solid ${borderSubtle}` : "none" }}>
            <div style={{ height: "2rem", borderBottom: `1px solid ${borderSubtle}`, display: "flex", alignItems: "center", justifyContent: "center", background: bgCardAlt }}>
              <span style={{ color: textSecondary, fontSize: "0.68rem", fontWeight: 600, padding: "0 0.3rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{court}</span>
            </div>
            <div style={{ position: "relative", height: `${totalH}px` }}>
              {timeSlots.map((_, i) => (
                <div key={i} style={{ position: "absolute", top: `${i * SLOT_H}px`, width: "100%", height: `${SLOT_H}px`, borderBottom: `1px solid ${borderSubtle}` }} />
              ))}
              {reservations.filter(r => r.court === ci).map((r, ri) => (
                <div key={ri} style={{
                  position: "absolute",
                  top: `${r.start * SLOT_H + 2}px`,
                  left: "3px",
                  right: "3px",
                  height: `${(r.end - r.start) * SLOT_H - 4}px`,
                  background: ri % 2 === 0 ? "rgba(91,140,255,0.15)" : "rgba(167,139,250,0.15)",
                  borderLeft: `2.5px solid ${ri % 2 === 0 ? blue : "#a78bfa"}`,
                  borderRadius: "0 0.25rem 0.25rem 0",
                  padding: "0.2rem 0.35rem",
                  overflow: "hidden",
                }}>
                  <span style={{ color: ri % 2 === 0 ? blue : "#a78bfa", fontSize: "0.62rem", fontWeight: 600, display: "block", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</span>
                  <span style={{ color: textDim, fontSize: "0.58rem" }}>{timeSlots[r.start]}–{r.end < timeSlots.length ? timeSlots[r.end] : "14:00"}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Stylised map tile behind the listing header. Hand-drawn rather than an
// image so it stays sharp at any width and ships no extra bytes.
const MapBackdrop: React.FC = () => (
  <svg viewBox="0 0 400 170" preserveAspectRatio="xMidYMid slice" style={{ width: "100%", height: "100%", display: "block" }} aria-hidden="true">
    <rect width="400" height="170" fill="#1a2333" />

    {/* Parks and water, the two things that make a map read as a map */}
    <path d="M0 116 L54 94 L118 110 L138 170 L0 170 Z" fill="#1c3329" />
    <path d="M306 0 L400 0 L400 48 L334 42 Z" fill="#1c3329" />
    <path d="M0 4 C64 20 122 -2 186 12 C248 26 312 6 400 20 L400 0 L0 0 Z" fill="#173049" />

    {/* City blocks */}
    <g fill="#212c3d">
      <rect x="24" y="40" width="52" height="24" rx="2" />
      <rect x="88" y="36" width="38" height="28" rx="2" />
      <rect x="236" y="34" width="46" height="30" rx="2" />
      <rect x="296" y="62" width="42" height="22" rx="2" />
      <rect x="46" y="136" width="34" height="20" rx="2" />
      <rect x="248" y="120" width="58" height="34" rx="2" />
      <rect x="330" y="112" width="46" height="26" rx="2" />
    </g>

    {/* Road network: two arterials, then the smaller grid */}
    <g fill="none" strokeLinecap="round">
      <path d="M-10 76 H410" stroke="#39485f" strokeWidth="10" />
      <path d="M214 -10 V180" stroke="#39485f" strokeWidth="10" />
      <path d="M-10 76 H410" stroke="#44546e" strokeWidth="3" />
      <path d="M214 -10 V180" stroke="#44546e" strokeWidth="3" />
      <g stroke="#2f3c52" strokeWidth="5">
        <path d="M-10 26 H410" />
        <path d="M-10 128 H410" />
        <path d="M86 -10 V180" />
        <path d="M320 -10 V180" />
        <path d="M232 108 L300 168" />
      </g>
    </g>

    {/* Selected place */}
    <ellipse cx="200" cy="120" rx="15" ry="4.5" fill="#000" opacity="0.35" />
    <g transform="translate(200,118)">
      <path d="M0 0 C-13 -17 -17 -25 -17 -33 A17 17 0 1 1 17 -33 C17 -25 13 -17 0 0 Z" fill="#34D399" />
      <circle cx="0" cy="-33" r="6" fill="#12321f" />
    </g>
  </svg>
);

// Mock of a venue's map listing, with the two fields Atlivo fills highlighted.
// Deliberately generic chrome and no Google marks: the point is which two slots
// take a link, not a pixel copy of anyone's product.
const MapsProfileWidget: React.FC = () => {
  const t = useTranslations("Landing");
  const actions = t.raw("Widgets.Maps.Actions") as string[];
  const actionIcons = [
    <Navigation key="0" className="w-4 h-4" />,
    <Bookmark key="1" className="w-4 h-4" />,
    <Phone key="2" className="w-4 h-4" />,
    <Share2 key="3" className="w-4 h-4" />,
  ];

  // The rows Atlivo fills in. Everything above them is the listing the owner
  // already has, so these are the only ones that carry an accent.
  const slot = (accent: string, icon: React.ReactNode, label: string, url: string) => (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "0.8rem",
      padding: "0.6rem 0.7rem",
      borderRadius: "0.6rem",
      background: `${accent}14`,
      border: `1px solid ${accent}4d`,
    }}>
      <span style={{ color: accent, display: "flex", flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <p style={{ color: textPrimary, fontSize: "0.8rem", fontWeight: 600 }}>{label}</p>
        <p style={{ color: textDim, fontSize: "0.68rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{url}</p>
      </div>
      <span style={{ marginLeft: "auto", flexShrink: 0, color: accent, background: `${accent}1f`, fontSize: "0.55rem", fontWeight: 700, letterSpacing: "0.09em", padding: "0.2rem 0.45rem", borderRadius: "9999px" }}>
        ATLIVO
      </span>
    </div>
  );

  const detail = (icon: React.ReactNode, children: React.ReactNode) => (
    <div className="flex items-center gap-3" style={{ padding: "0.35rem 0.7rem" }}>
      <span style={{ color: textDim, display: "flex", flexShrink: 0 }}>{icon}</span>
      <p style={{ fontSize: "0.76rem", color: textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{children}</p>
    </div>
  );

  return (
    <div style={{ background: bgCard, border: `1px solid ${border}`, borderRadius: "1rem", overflow: "hidden", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.6)" }}>
      {/* Map + the search that got the athlete here */}
      <div style={{ position: "relative", height: "10rem" }}>
        <MapBackdrop />
        <div style={{
          position: "absolute",
          top: "0.85rem",
          left: "0.85rem",
          right: "0.85rem",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          background: "#2b3648",
          border: `1px solid ${borderSubtle}`,
          borderRadius: "9999px",
          padding: "0.5rem 0.9rem",
          boxShadow: "0 6px 16px -6px rgba(0,0,0,0.7)",
        }}>
          <Search className="w-3.5 h-3.5" style={{ color: textMuted, flexShrink: 0 }} />
          <span style={{ color: textSecondary, fontSize: "0.78rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {t("Widgets.Maps.SearchQuery")}
          </span>
        </div>
      </div>

      <div style={{ padding: "1rem 1.1rem 1.2rem" }}>
        <p style={{ color: textPrimary, fontWeight: 700, fontSize: "1.05rem" }}>{t("Widgets.Maps.VenueName")}</p>
        <div className="flex items-center gap-1.5" style={{ marginTop: "0.3rem" }}>
          <span style={{ color: textSecondary, fontSize: "0.78rem", fontWeight: 600 }}>4,8</span>
          <span className="flex items-center gap-0.5">
            {[0, 1, 2, 3, 4].map(i => (
              <Star key={i} className="w-3 h-3" style={{ color: "#FBBF24", fill: "#FBBF24" }} />
            ))}
          </span>
          <span style={{ color: textDim, fontSize: "0.72rem" }}>(128)</span>
        </div>
        <p style={{ color: textMuted, fontSize: "0.75rem", marginTop: "0.25rem" }}>{t("Widgets.Maps.Category")}</p>

        {/* The listing's own buttons. Muted on purpose: not what this is about */}
        <div className="flex items-start" style={{ gap: "1.35rem", margin: "1.1rem 0 1rem" }}>
          {actions.map((a, i) => (
            <div key={a} className="flex flex-col items-center" style={{ gap: "0.35rem", width: "3.1rem" }}>
              <span style={{
                width: "2.1rem",
                height: "2.1rem",
                borderRadius: "9999px",
                border: `1px solid ${i === 0 ? "transparent" : borderSubtle}`,
                background: i === 0 ? blue : "transparent",
                color: i === 0 ? "#0e1014" : blue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                {actionIcons[i]}
              </span>
              <span style={{ color: textMuted, fontSize: "0.62rem", textAlign: "center" }}>{a}</span>
            </div>
          ))}
        </div>

        <div style={{ borderTop: `1px solid ${borderSubtle}`, paddingTop: "0.5rem", marginBottom: "0.5rem" }}>
          {detail(<MapPin className="w-3.5 h-3.5" />, t("Widgets.Maps.Address"))}
          {detail(<Clock className="w-3.5 h-3.5" />, (
            <>
              <span style={{ color: green, fontWeight: 600 }}>{t("Widgets.Maps.Hours")}</span>
              {" · "}
              {t("Widgets.Maps.HoursDetail")}
            </>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {slot(green, <CalendarCheck className="w-4 h-4" />, t("Widgets.Maps.BookingSlot"), t("GoogleMaps.Links.Booking.Url"))}
          {slot(blue, <Globe className="w-4 h-4" />, t("Widgets.Maps.WebsiteSlot"), t("GoogleMaps.Links.Info.Url"))}
        </div>
      </div>
    </div>
  );
};

// Purely presentational. Signed-in visitors are redirected server-side in
// page.tsx before this ever renders, so there is no auth state to wait on and
// the markup is complete in the first HTML response.
const LandingView: React.FC = () => {
  const t = useTranslations("Landing");

  return (
    <div style={{ color: textPrimary, minHeight: "100vh", fontFamily: "system-ui, -apple-system, sans-serif" }} className="bg-gradient-to-b from-gray-900 to-gray-800">

      <MarketingNavBar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-24 pb-24">
        <div className="text-center" style={{ maxWidth: "780px", margin: "0 auto" }}>
          <p style={{ color: textMuted, fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: "1.75rem", fontWeight: 500 }}>
            {t("Hero.Tagline")}
          </p>
          <h1 style={{ fontSize: "clamp(2.6rem, 5.5vw, 4.5rem)", fontWeight: 800, lineHeight: 1.1, marginBottom: "1.75rem", letterSpacing: "-0.02em" }}>
            {t("Hero.Heading")}<br />
            <span style={{ color: green }}>{t("Hero.AthletesPart")}</span>
            {" "}{t("Hero.BookPart")}<br />
            <span style={{ color: blue }}>{t("Hero.VenuesPart")}</span>
            {" "}{t("Hero.GrowPart")}
          </h1>
          <p style={{ color: textMuted, fontSize: "1.1rem", lineHeight: 1.75, maxWidth: "600px", margin: "0 auto 2.5rem" }}>
            {t("Hero.Description")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/sign-up" className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg px-8 py-3.5 transition-colors text-base">
              {t("Hero.WantToBook")}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link href="/pre-registration" className="flex items-center justify-center gap-2 bg-transparent text-gray-300 font-semibold rounded-lg px-8 py-3.5 border border-gray-700 hover:border-gray-500 hover:text-white transition-colors text-base">
              {t("Hero.RunAVenue")}
            </Link>
          </div>
        </div>
      </section>

      {/* For Athletes */}
      <section className="py-24 border-t border-gray-700/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p style={{ color: green, fontSize: "0.72rem", letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: "1rem", fontWeight: 600 }}>
                {t("Athletes.Label")}
              </p>
              <h2 style={{ fontSize: "clamp(1.7rem, 3vw, 2.5rem)", fontWeight: 800, lineHeight: 1.2, marginBottom: "1.25rem", letterSpacing: "-0.01em" }}>
                {t("Athletes.Headline")}
              </h2>
              <p style={{ color: textMuted, fontSize: "1rem", lineHeight: 1.8, marginBottom: "2rem" }}>
                {t("Athletes.Description")}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.875rem" }}>
                {[t("Athletes.Features.RealTime"), t("Athletes.Features.Recurring"), t("Athletes.Features.Free")].map(f => (
                  <li key={f} className="flex items-start gap-3">
                    <Check style={{ color: green, flexShrink: 0, marginTop: "0.15rem" }} className="w-4 h-4" />
                    <span style={{ color: textSecondary, fontSize: "0.9rem" }}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <BookingWidget />
          </div>
        </div>
      </section>

      {/* For Venue Owners */}
      <section className="py-24 border-t border-gray-700/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <ReservationWidget />
            </div>
            <div className="order-1 lg:order-2">
              <p style={{ color: blue, fontSize: "0.72rem", letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: "1rem", fontWeight: 600 }}>
                {t("VenueOwners.Label")}
              </p>
              <h2 style={{ fontSize: "clamp(1.7rem, 3vw, 2.5rem)", fontWeight: 800, lineHeight: 1.2, marginBottom: "1.25rem", letterSpacing: "-0.01em" }}>
                {t("VenueOwners.Headline")}
              </h2>
              <p style={{ color: textMuted, fontSize: "1rem", lineHeight: 1.8, marginBottom: "2rem" }}>
                {t("VenueOwners.Description")}
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.875rem", marginBottom: "2rem" }}>
                {[t("VenueOwners.Features.Calendar"), t("VenueOwners.Features.Reach"), t("VenueOwners.Features.NoCommission")].map(f => (
                  <li key={f} className="flex items-start gap-3">
                    <Check style={{ color: blue, flexShrink: 0, marginTop: "0.15rem" }} className="w-4 h-4" />
                    <span style={{ color: textSecondary, fontSize: "0.9rem" }}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link href="/pre-registration" className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg px-8 py-3.5 transition-colors text-base">
                {t("VenueOwners.JoinButton")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* Google Maps links */}
      <section className="py-24 border-t border-gray-700/50 bg-gray-900/40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p style={{ color: blue, fontSize: "0.72rem", letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: "1rem", fontWeight: 600 }}>
                {t("GoogleMaps.Label")}
              </p>
              <h2 style={{ fontSize: "clamp(1.7rem, 3vw, 2.5rem)", fontWeight: 800, lineHeight: 1.2, marginBottom: "1.25rem", letterSpacing: "-0.01em" }}>
                {t("GoogleMaps.Headline")}
              </h2>
              <p style={{ color: textMuted, fontSize: "1rem", lineHeight: 1.8, marginBottom: "2rem" }}>
                {t("GoogleMaps.Description")}
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem", marginBottom: "2rem" }}>
                {[
                  { accent: green, icon: <CalendarCheck className="w-4 h-4" />, key: "Booking" },
                  { accent: blue, icon: <Globe className="w-4 h-4" />, key: "Info" },
                ].map(({ accent, icon, key }) => (
                  <div key={key} className="flex items-start gap-3">
                    <span style={{ color: accent, flexShrink: 0, marginTop: "0.2rem", display: "flex" }}>{icon}</span>
                    <div>
                      <p style={{ color: textPrimary, fontWeight: 700, fontSize: "0.95rem", marginBottom: "0.3rem" }}>
                        {t(`GoogleMaps.Links.${key}.Title`)}
                      </p>
                      <p style={{ color: textSecondary, fontSize: "0.9rem", lineHeight: 1.7 }}>
                        {t(`GoogleMaps.Links.${key}.Description`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <p style={{ color: textDim, fontSize: "0.82rem", lineHeight: 1.7 }}>
                {t("GoogleMaps.Footnote")}
              </p>
            </div>

            <MapsProfileWidget />
          </div>
        </div>
      </section>

      {/* Pick a side */}
      <section className="py-28 border-t border-gray-700/50 bg-gray-900/60">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)", fontWeight: 800, marginBottom: "1rem", letterSpacing: "-0.02em" }}>
            {t("FinalCTA.Title")}
          </h2>
          <p style={{ color: textMuted, fontSize: "1.05rem", marginBottom: "3rem" }}>
            {t("FinalCTA.Subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/sign-up" className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg px-9 py-4 transition-colors text-base">
              {t("FinalCTA.JoinAsAthlete")}
            </Link>
            <Link href="/pre-registration" className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg px-9 py-4 transition-colors text-base">
              {t("FinalCTA.ListYourVenue")}
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default LandingView;
